import type { Inputs } from './types';
import { MAX_YEARS } from './pension';

/** Keyed by form field id, not by Inputs shape — stages are nested there but flat here. */
export interface FieldErrors {
  currentAge?: string;
  retirementAge?: string;
  currentPot?: string;
  stage1Contribution?: string;
  stage2Years?: string;
  stage2Contribution?: string;
  stage3Years?: string;
  stage3Contribution?: string;
  growthRate?: string;
  inflationRate?: string;
  withdrawalRate?: string;
}

const AGE_MIN = 1;
const AGE_MAX = 100;

/** Pure input validation — no DOM, so it can be unit tested alongside the engine. */
export function validate(input: Inputs): FieldErrors {
  const errors: FieldErrors = {};

  if (!isInt(input.currentAge) || input.currentAge < AGE_MIN || input.currentAge > AGE_MAX) {
    errors.currentAge = `Enter a whole age between ${AGE_MIN} and ${AGE_MAX}.`;
  }
  if (!isInt(input.retirementAge) || input.retirementAge > AGE_MAX) {
    errors.retirementAge = `Enter a whole age no greater than ${AGE_MAX}.`;
  } else if (!errors.currentAge && input.retirementAge <= input.currentAge) {
    errors.retirementAge = 'Retirement age must be greater than your current age.';
  } else if (!errors.currentAge && input.retirementAge - input.currentAge > MAX_YEARS) {
    errors.retirementAge = `That's more than ${MAX_YEARS} years away.`;
  }
  if (!isMoney(input.currentPot)) {
    errors.currentPot = 'Enter an amount of 0 or more.';
  }

  // Stage 1 has no years of its own — it balances out the other two.
  const [stage1, stage2, stage3] = input.stages;
  if (!isMoney(stage1.annualContribution)) {
    errors.stage1Contribution = 'Enter an amount of 0 or more.';
  }
  if (!isStageYears(stage2.years)) {
    errors.stage2Years = 'Enter a whole number of years, 0 or more.';
  }
  if (!isMoney(stage2.annualContribution)) {
    errors.stage2Contribution = 'Enter an amount of 0 or more.';
  }
  if (!isStageYears(stage3.years)) {
    errors.stage3Years = 'Enter a whole number of years, 0 or more.';
  }
  if (!isMoney(stage3.annualContribution)) {
    errors.stage3Contribution = 'Enter an amount of 0 or more.';
  }

  // Coast and Top up are carved out of the runway to retirement, and Build takes
  // what's left, so the two of them together can't be longer than the runway.
  // Only worth saying once the ages it's measured against are themselves valid.
  const agesUsable = !errors.currentAge && !errors.retirementAge;
  if (agesUsable && !errors.stage2Years && !errors.stage3Years) {
    const horizon = input.retirementAge - input.currentAge;
    if (stage3.years > horizon) {
      errors.stage3Years = `Longer than the ${plural(horizon)} until you retire.`;
    } else if (stage2.years + stage3.years > horizon) {
      errors.stage2Years = `Coast and Top up together run past retirement — you have ${plural(horizon)}.`;
    }
  }

  if (!isRate(input.growthRate, -0.5, 0.5)) {
    errors.growthRate = 'Enter a growth rate between -50% and 50%.';
  }
  if (!isRate(input.inflationRate, -0.1, 0.5)) {
    errors.inflationRate = 'Enter an inflation rate between -10% and 50%.';
  }
  if (!isRate(input.withdrawalRate, 0, 0.2)) {
    errors.withdrawalRate = 'Enter a withdrawal rate between 0% and 20%.';
  }

  return errors;
}

export function hasErrors(errors: FieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

const plural = (years: number) => `${years} ${years === 1 ? 'year' : 'years'}`;
const isInt = (n: number) => Number.isInteger(n);
const isMoney = (n: number) => Number.isFinite(n) && n >= 0 && n <= 1e12;
const isStageYears = (n: number) => Number.isInteger(n) && n >= 0 && n <= MAX_YEARS;
const isRate = (n: number, min: number, max: number) =>
  Number.isFinite(n) && n >= min && n <= max;
